import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, FileText, AlertCircle, Filter, RefreshCw, Clock, ChevronDown } from 'lucide-react';
import { formatPhoneNumber } from '@/lib/utils';

interface LogEntry {
  id: string;
  type: string;
  message: string;
  customer_id: string | null;
  customer_name?: string;
  customer_phone?: string;
  created_at: string;
}

export const LogsManager: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [displayedLogs, setDisplayedLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [logType, setLogType] = useState<string>('all');
  const [customerDetails, setCustomerDetails] = useState<Record<string, { name?: string, phone?: string }>>({});
  const [visibleCount, setVisibleCount] = useState(5);
  const [hasMore, setHasMore] = useState(false);

  // Fetch logs
  const fetchLogs = async () => {
    try {
      setLoading(true);
      
      // Query staff_notifications table
      const { data, error } = await supabase
        .from('staff_notifications')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Get unique customer IDs to fetch their details
      const customerIds = data
        .filter(log => log.customer_id)
        .map(log => log.customer_id);
      
      const uniqueCustomerIds = [...new Set(customerIds)];
      
      // Fetch customer details in bulk
      if (uniqueCustomerIds.length > 0) {
        const { data: customers, error: customerError } = await supabase
          .from('customers')
          .select('id, name, phone_number')
          .in('id', uniqueCustomerIds);
        
        if (!customerError && customers) {
          // Build a lookup map
          const customerMap: Record<string, { name?: string, phone?: string }> = {};
          customers.forEach(customer => {
            customerMap[customer.id] = {
              name: customer.name,
              phone: customer.phone_number
            };
          });
          setCustomerDetails(customerMap);
        }
      }
      
      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    
    // Set up a refresh interval every 30 seconds
    const intervalId = setInterval(fetchLogs, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Filter logs and update displayed logs when filters change
  useEffect(() => {
    // Filter logs based on search query and type
    const filtered = logs.filter(log => {
      // Filter by log type
      if (logType !== 'all' && log.type !== logType) {
        return false;
      }
      
      // Filter by search query
      if (searchQuery) {
        const customerInfo = customerDetails[log.customer_id || ''];
        const searchLower = searchQuery.toLowerCase();
        
        return (
          log.message.toLowerCase().includes(searchLower) ||
          (customerInfo?.name && customerInfo.name.toLowerCase().includes(searchLower)) ||
          (customerInfo?.phone && customerInfo.phone.toLowerCase().includes(searchLower)) ||
          log.type.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });

    // Reset visible count when filters change
    setVisibleCount(5);
    
    // Update displayed logs and check if there are more logs to show
    setDisplayedLogs(filtered.slice(0, 5));
    setHasMore(filtered.length > 5);
  }, [logs, searchQuery, logType, customerDetails]);

  // Load more logs
  const loadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      const nextBatch = 10;
      const newVisibleCount = visibleCount + nextBatch;
      
      // Get filtered logs
      const filtered = logs.filter(log => {
        if (logType !== 'all' && log.type !== logType) return false;
        
        if (searchQuery) {
          const customerInfo = customerDetails[log.customer_id || ''];
          const searchLower = searchQuery.toLowerCase();
          
          return (
            log.message.toLowerCase().includes(searchLower) ||
            (customerInfo?.name && customerInfo.name.toLowerCase().includes(searchLower)) ||
            (customerInfo?.phone && customerInfo.phone.toLowerCase().includes(searchLower)) ||
            log.type.toLowerCase().includes(searchLower)
          );
        }
        
        return true;
      });
      
      // Update displayed logs
      setDisplayedLogs(filtered.slice(0, newVisibleCount));
      setVisibleCount(newVisibleCount);
      setHasMore(filtered.length > newVisibleCount);
      setLoadingMore(false);
    }, 300);
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  };

  // Get customer information
  const getCustomerInfo = (customerId: string | null) => {
    if (!customerId) return 'N/A';
    
    const customer = customerDetails[customerId];
    if (!customer) return 'Unknown';
    
    if (customer.name) {
      return `${customer.name} (${formatPhoneNumber(customer.phone || '')})`;
    } else {
      return formatPhoneNumber(customer.phone || '');
    }
  };

  // Get log type display name and icon
  const getLogTypeInfo = (type: string) => {
    switch (type) {
      case 'scan':
        return { name: 'Customer Scan', icon: <Clock className="h-4 w-4 text-blue-500" /> };
      case 'reward_redeemed':
        return { name: 'Reward Redeemed', icon: <FileText className="h-4 w-4 text-green-500" /> };
      case 'error':
        return { name: 'Error', icon: <AlertCircle className="h-4 w-4 text-red-500" /> };
      default:
        return { name: type.charAt(0).toUpperCase() + type.slice(1), icon: <FileText className="h-4 w-4 text-gray-500" /> };
    }
  };

  return (
    <div className="space-y-4">
      {/* Search and filter bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search logs..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={logType} onValueChange={setLogType}>
            <SelectTrigger className="w-[140px]">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <span>Filter by type</span>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="scan">Customer Scan</SelectItem>
              <SelectItem value="reward_redeemed">Reward Redeemed</SelectItem>
              <SelectItem value="error">Errors</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            onClick={fetchLogs}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>
      
      {/* Logs table */}
      <Card>
        <CardContent className="p-0">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="w-[180px]">Date/Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center">
                      {loading ? (
                        <div className="flex justify-center items-center">
                          <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                          Loading logs...
                        </div>
                      ) : (
                        "No logs found"
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedLogs.map((log) => {
                    const { name: typeName, icon } = getLogTypeInfo(log.type);
                    
                    return (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1">
                            {icon}
                            <span>{typeName}</span>
                          </div>
                        </TableCell>
                        <TableCell>{log.message}</TableCell>
                        <TableCell>{getCustomerInfo(log.customer_id)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDate(log.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            
            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center p-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full max-w-xs"
                >
                  {loadingMore ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  )}
                  Load More Logs
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 